/**
 * Third-party notices — the contract between the generator and the two
 * licences screens.
 *
 * `scripts/generate-third-party-notices.mjs` derives one JSON per shipped app
 * (`apps/<app>/src/legal/thirdPartyNotices.generated.json`) from the lockfile,
 * the installed packages and the native register. Both apps read that JSON
 * through the schema and helpers here, so the file's shape has one home and
 * the screens can be tested without a renderer: the view model is data.
 *
 * Why the apps carry this at all: Apache-2.0 §4(a), MIT, ISC and the BSD
 * family all require whoever ships a binary built on the code to reproduce
 * its copyright notice and licence text (docs/legal/LICENSE-CLEARANCE.md,
 * rows 4, 9, 10).
 */

import { z } from 'zod';

/** Where a component sits in the binary: npm tree, or a native layer. */
export const NOTICE_LAYERS = ['npm', 'native', 'android', 'ios', 'model'] as const;
export type NoticeLayer = (typeof NOTICE_LAYERS)[number];

export const ThirdPartyNoticeSchema = z.object({
  name: z.string().min(1),
  version: z.string().min(1),
  /** The licence as declared (an SPDX expression, or a LicenseRef). */
  license: z.string().min(1),
  /** For `A OR B` licences: the one taken, whose text is reproduced. */
  elected: z.string().min(1).optional(),
  /** Copyright lines as found in the component's own licence file. */
  copyright: z.array(z.string()),
  /** The declared author, recorded only when no copyright line exists. */
  author: z.string().min(1).optional(),
  /** Keys into `texts`: an SPDX id for a standard text, `v:<hash>` for a verbatim file. */
  texts: z.array(z.string().min(1)),
  note: z.string().min(1).optional(),
  /** A change made to the component (stated even where the licence does not require it). */
  modified: z.string().min(1).optional(),
  layer: z.enum(NOTICE_LAYERS),
  /** For native components: the artifact ids that carry it. */
  artifacts: z.array(z.string()).optional(),
});
export type ThirdPartyNotice = z.infer<typeof ThirdPartyNoticeSchema>;

export const ThirdPartyNoticesFileSchema = z.object({
  app: z.string().min(1),
  generatedBy: z.string().min(1),
  entries: z.array(ThirdPartyNoticeSchema),
  texts: z.record(z.string(), z.string()),
});
export type ThirdPartyNoticesFile = z.infer<typeof ThirdPartyNoticesFileSchema>;

/** One row of a licences list, ready to render. */
export interface NoticeRow {
  /** Stable, unique within one file: `layer:name@version`. */
  key: string;
  name: string;
  version: string;
  /** `MIT`, or `BSD-3-Clause (elected from BSD-3-Clause OR GPL-2.0)`. */
  licence: string;
  /** The copyright lines joined, the author as a fallback, or ''. */
  copyright: string;
  layer: NoticeLayer;
  note?: string;
  modified?: string;
  artifacts?: string[];
}

/** The one-line intro both screens open with. */
export const NOTICES_INTRO =
  'This app is built with open-source software. The notices below are reproduced as their licences require. Tap a component to read its licence text.';

/** Shown in place of a licence text the file does not carry. */
export const NOTICE_TEXT_UNAVAILABLE =
  'The licence text for this component is not available in this build. See THIRD_PARTY_NOTICES.md in the source repository.';

export function licenceLabel(n: Pick<ThirdPartyNotice, 'license' | 'elected'>): string {
  return n.elected ? `${n.elected} (elected from ${n.license})` : n.license;
}

export function noticeKey(n: Pick<ThirdPartyNotice, 'layer' | 'name' | 'version'>): string {
  return `${n.layer}:${n.name}@${n.version}`;
}

/**
 * Rows for the list, sorted by name (case-insensitively) then version — the
 * generator's own order, restated here so a hand-edited or re-ordered file
 * still renders alphabetically. Empty input → empty list, never a throw.
 */
export function noticeRows(file: Pick<ThirdPartyNoticesFile, 'entries'>): NoticeRow[] {
  return [...file.entries]
    .sort(
      (a, b) =>
        a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }) ||
        a.version.localeCompare(b.version, 'en'),
    )
    .map((n) => {
      const row: NoticeRow = {
        key: noticeKey(n),
        name: n.name,
        version: n.version,
        licence: licenceLabel(n),
        copyright:
          n.copyright.length > 0 ? n.copyright.join(' · ') : n.author ? `Author: ${n.author}` : '',
        layer: n.layer,
      };
      if (n.note) row.note = n.note;
      if (n.modified) row.modified = n.modified;
      if (n.artifacts) row.artifacts = n.artifacts;
      return row;
    });
}

/**
 * The licence text(s) behind one row: each referenced text once, in the
 * entry's order, separated by a rule. A dangling reference renders as the
 * unavailable sentence rather than nothing — a blank panel would read as
 * "no licence".
 */
export function noticeText(
  file: Pick<ThirdPartyNoticesFile, 'entries' | 'texts'>,
  key: string,
): string {
  const entry = file.entries.find((n) => noticeKey(n) === key);
  if (!entry || entry.texts.length === 0) return NOTICE_TEXT_UNAVAILABLE;
  const seen = new Set<string>();
  const parts: string[] = [];
  for (const ref of entry.texts) {
    if (seen.has(ref)) continue;
    seen.add(ref);
    const text = file.texts[ref];
    parts.push(text === undefined ? NOTICE_TEXT_UNAVAILABLE : text.trimEnd());
  }
  return parts.join('\n\n────────\n\n');
}

/** Expand/collapse as an immutable set operation, so it is trivially testable. */
export function toggleExpanded(expanded: ReadonlySet<string>, key: string): ReadonlySet<string> {
  const next = new Set(expanded);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

/** `240 components under 10 licences` — the summary line above the list. */
export function noticeSummary(file: Pick<ThirdPartyNoticesFile, 'entries'>): string {
  const count = file.entries.length;
  const licences = new Set(file.entries.map(licenceLabel)).size;
  if (count === 0) return 'No third-party components are recorded for this build.';
  return `${count} component${count === 1 ? '' : 's'} under ${licences} licence${licences === 1 ? '' : 's'}`;
}
